import React from 'react'
import { Banner } from './banner_types'
import { BannerFrame, BannerImage } from './banner_frame'

/*
 * Left / right side rail.
 *
 * Layout-level, not content-level: the rail is a sibling of the central Patek
 * column — the `.container` that carries the whole page (see apps/_root.tsx and
 * component/banner/side_banner_rails.tsx) — so it lives in the page's outer
 * space and never sits inside, or on top of, the hero, the info row or the game
 * lists below them. It stays sticky while the page content scrolls, and stops
 * where the content stops rather than trailing down over the footer.
 *
 * One card = one API banner filling it completely (see .page-rail-item in
 * circus.css, PHASE 25). The card's height comes from the page section, so both
 * rails start level with the hero and finish level with the bottom of the info
 * row (.randing-device-change); its width is then that height at the artwork's
 * own vertical 1:2, so the banner is shown whole and nothing is cropped. The
 * page column gives up the width this needs — it is the rails that are sized
 * off the artwork, not the artwork that is squeezed into what is left over.
 *
 * Left and right share the same CSS and the same --rail-count, so both rails
 * always render at identical width, height and spacing.
 *
 * Multiple banners stack in API order; the rail is deliberately not a carousel.
 */

interface SideBannerRailProps {
    side: 'left' | 'right'
    banners: Banner[]
}

export default function SideBannerRail({ side, banners }: SideBannerRailProps) {
    // No banners → no rail element at all, so no column and no reserved width.
    // A side the API left empty renders nothing, not an empty card.
    if (banners.length === 0) return null

    return (
        <aside className={`page-rail page-rail--${side}`}>
            {banners.map((banner, index) => (
                <BannerFrame key={banner.id} banner={banner} className="page-rail-item">
                    {/* The rail's first card is at the top of the page, so it
                        must not be deferred; only the ones stacked below can. */}
                    <BannerImage banner={banner}
                        eager={index === 0}
                        priority={index === 0 ? 'low' : undefined} />
                </BannerFrame>
            ))}
        </aside>
    )
}
